import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { SiteShell } from "@/components/site/SiteShell";
import { PageHero } from "@/components/site/PageHero";
import { SEO, breadcrumbSchema, faqSchema } from "@/components/site/SEO";
import { FAQ } from "@/components/site/FAQ";
import { Check, Star, ArrowRight, Calendar, Mail, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RETAINER_PACKAGES, PRICING_FAQS, ONE_OFF_PROJECT_TYPES } from "@/lib/pricingData";
import { SITE } from "@/lib/siteData";
import { trackLeadSubmission, getGaClientId } from "@/lib/analytics";
import { ROUTE_SEO } from "@/lib/routeSeo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CALENDLY_URL = "https://calendly.com/pie-in-the-sky-productions/15min";

export default function Pricing() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", company: "", project_type: "", budget: "", timeline: "", brief: "" });
  const [submitting, setSubmitting] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.brief.trim()) { toast.error("Please fill in your name, email and a short project brief."); return; }
    setSubmitting(true);
    try {
      const payload = { ...form, source: "pricing-one-off" };
      Object.keys(payload).forEach(k => payload[k] === "" && delete payload[k]);
      const gaCid = getGaClientId();
      if (gaCid) payload.ga_client_id = gaCid;
      await axios.post(`${BACKEND_URL}/api/enquiry`, payload);
      trackLeadSubmission({ source: "pricing-page", formName: "Pricing page brief form", value: 750 });
      toast.success("Brief received - we'll reply within 1 business day to info@pieinthesky.co.za.");
      setForm({ name: "", email: "", company: "", project_type: "", budget: "", timeline: "", brief: "" });
      navigate("/thank-you?source=pricing-page");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again or email us directly.");
    } finally { setSubmitting(false); }
  };

  return (
    <SiteShell>
      <SEO
        title={ROUTE_SEO["/pricing"].title}
        description={ROUTE_SEO["/pricing"].meta}
        path="/pricing"
        jsonLd={[
          breadcrumbSchema([{name: "Home", path: "/"}, {name: "Pricing"}]),
          faqSchema(PRICING_FAQS),
        ]}
      />
      <PageHero kicker="Plans & Pricing" title={ROUTE_SEO["/pricing"].h1} subtitle="Need a one-off project? Use the custom quote form below. Looking for ongoing content? Our monthly retainer packages start at R7,500." breadcrumb={[{name: "Home", path: "/"}, {name: "Pricing"}]} />

      {/* One-off project form */}
      <section id="one-off" className="section-pad">
        <div className="container-site grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <p className="kicker mb-3">Custom quote</p>
            <h2 className="font-display uppercase text-foreground text-[clamp(2rem,5vw,3.5rem)] leading-[0.95]">Get a custom quote.</h2>
            <p className="mt-5 text-sm sm:text-base text-muted-foreground leading-relaxed max-w-md">For single-shot commercials, brand films, events, music videos or photography projects - share your brief and we'll send a line-item quote within 1 business day.</p>
            <p className="mt-6 text-sm text-muted-foreground">Or email us directly at <a href={`mailto:${SITE.email}`} className="text-foreground hover:text-[hsl(var(--primary))]">{SITE.email}</a></p>
          </div>
          <div className="lg:col-span-7">
            <form data-testid="pricing-enquiry-form" onSubmit={submit} className="rounded-2xl border border-border bg-card p-6 sm:p-8" noValidate>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Name</Label><Input data-testid="one-off-name" value={form.name} onChange={upd("name")} placeholder="Your name" className="bg-background border-border h-11" required /></div>
                <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Email</Label><Input data-testid="one-off-email" type="email" value={form.email} onChange={upd("email")} placeholder="you@brand.com" className="bg-background border-border h-11" required /></div>
                <div className="space-y-2 sm:col-span-2"><Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Company</Label><Input data-testid="one-off-company" value={form.company} onChange={upd("company")} placeholder="Brand or agency" className="bg-background border-border h-11" /></div>
                <div className="space-y-2 sm:col-span-2">
                  <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Project type</Label>
                  <div className="flex flex-wrap gap-2">{ONE_OFF_PROJECT_TYPES.map(t => {
                    const active = form.project_type === t;
                    return <button type="button" key={t} onClick={() => setForm({ ...form, project_type: active ? "" : t })} className={`h-9 px-4 rounded-full text-xs uppercase tracking-[0.18em] border transition-colors ${active ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-transparent" : "bg-background border-border text-muted-foreground hover:text-foreground"}`}>{t}</button>;
                  })}</div>
                </div>
                <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Budget</Label><Input data-testid="one-off-budget" value={form.budget} onChange={upd("budget")} placeholder="e.g. R50k - R150k" className="bg-background border-border h-11" /></div>
                <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Timeline</Label><Input data-testid="one-off-timeline" value={form.timeline} onChange={upd("timeline")} placeholder="e.g. 4 weeks" className="bg-background border-border h-11" /></div>
                <div className="space-y-2 sm:col-span-2"><Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Project brief</Label><Textarea data-testid="one-off-brief" value={form.brief} onChange={upd("brief")} placeholder="What are you making, who's it for, when do you need it?" className="bg-background border-border min-h-[140px]" required /></div>
              </div>
              <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <p className="text-xs text-muted-foreground">We reply within 1 business day. Or email <a href={`mailto:${SITE.email}`} className="text-foreground hover:text-[hsl(var(--primary))]">{SITE.email}</a> directly.</p>
                <Button type="submit" disabled={submitting} data-testid="one-off-submit" className="h-12 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-110 px-6 disabled:opacity-60">{submitting ? "Sending…" : (<>Send brief <Send className="ml-2 h-4 w-4" /></>)}</Button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Retainer cards */}
      <section className="section-pad">
        <div className="container-site">
          <div className="text-center mb-12 sm:mb-16">
            <p className="kicker mb-3">Monthly retainer packages</p>
            <h2 className="font-display uppercase text-foreground text-[clamp(2rem,5vw,3.5rem)] leading-[0.95]">Pick the engine size.</h2>
            <p className="mt-4 max-w-2xl mx-auto text-sm sm:text-base text-muted-foreground">Every package: senior crew, cinema-grade kit, in-house post. 3-month minimum. Pause after with 30 days' notice.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            {RETAINER_PACKAGES.map((pkg, i) => (
              <motion.div
                key={pkg.id}
                data-testid={`pricing-card-${pkg.id}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.06 }}
                className={`relative rounded-2xl border ${pkg.featured ? "border-[hsl(var(--primary))/0.6] bg-[hsl(225,18%,8%)] shadow-[0_30px_80px_rgba(0,0,0,0.4)]" : "border-border bg-card"} p-6 sm:p-7 flex flex-col`}
              >
                {pkg.badge && <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] px-3 py-1 text-[10px] uppercase tracking-[0.22em]"><Star className="h-3 w-3 fill-current" />{pkg.badge}</div>}
                <h3 className="font-display text-xl uppercase tracking-[0.02em] text-foreground">{pkg.name}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{pkg.headline}</p>
                <div className="mt-5 flex items-baseline gap-1.5">
                  <span className="font-display text-4xl sm:text-5xl text-foreground">{pkg.priceLabel}</span>
                  <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground">/mo</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{pkg.minimum}</p>
                <ul className="mt-6 space-y-3 flex-1">
                  {pkg.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-sm text-foreground/85 leading-relaxed"><Check className="h-4 w-4 text-[hsl(var(--primary))] mt-0.5 shrink-0" />{f}</li>
                  ))}
                </ul>
                <Button asChild className={`mt-7 h-11 w-full rounded-full ${pkg.featured ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-110" : "bg-foreground/95 text-background hover:bg-foreground"}`}>
                  <a href={CALENDLY_URL} target="_blank" rel="noreferrer noopener" data-testid={`pricing-cta-${pkg.id}`}>{pkg.cta} <ArrowRight className="ml-2 h-4 w-4" /></a>
                </Button>
              </motion.div>
            ))}
          </div>
          <p className="text-center mt-10 text-xs text-muted-foreground">All prices in ZAR. Need 6 or 12-month pricing? <a href={CALENDLY_URL} target="_blank" rel="noreferrer noopener" className="text-foreground hover:text-[hsl(var(--primary))] underline underline-offset-4">Book a 15-min call</a> for tailored rates.</p>

          {/* Industry-specific landing page CTAs */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-7xl mx-auto">
            <Link
              to="/for/hotels-and-lodges"
              data-testid="pricing-hotels-cta"
              className="group flex flex-col justify-between gap-3 rounded-2xl border border-[hsl(var(--primary))/0.35] bg-[hsl(225,18%,8%)] px-6 py-5 hover:border-[hsl(var(--primary))/0.7] transition-colors min-h-[180px]"
            >
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary))]">For Hotels & Lodges</p>
                <p className="mt-2 font-display text-base uppercase tracking-[0.02em] text-foreground leading-tight">Run a hotel or lodge? See our hospitality packages.</p>
                <p className="mt-1.5 text-xs text-muted-foreground">Cinematic production + SEO + AI search - built for direct bookings.</p>
              </div>
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-foreground group-hover:text-[hsl(var(--primary))] transition-colors">View hotel packages <ArrowRight className="h-3 w-3" /></span>
            </Link>

            <Link
              to="/for/marketing-agencies"
              data-testid="pricing-agencies-cta"
              className="group flex flex-col justify-between gap-3 rounded-2xl border border-[hsl(var(--primary))/0.35] bg-[hsl(225,18%,8%)] px-6 py-5 hover:border-[hsl(var(--primary))/0.7] transition-colors min-h-[180px]"
            >
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary))]">For Marketing Agencies</p>
                <p className="mt-2 font-display text-base uppercase tracking-[0.02em] text-foreground leading-tight">Agency partner? White-label production at scale.</p>
                <p className="mt-1.5 text-xs text-muted-foreground">Senior crew, NDA-friendly workflows and rev-share models for agencies.</p>
              </div>
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-foreground group-hover:text-[hsl(var(--primary))] transition-colors">View agency packages <ArrowRight className="h-3 w-3" /></span>
            </Link>

            <Link
              to="/for/medical-dental-pharma"
              data-testid="pricing-medical-cta"
              className="group flex flex-col justify-between gap-3 rounded-2xl border border-[hsl(var(--primary))/0.35] bg-[hsl(225,18%,8%)] px-6 py-5 hover:border-[hsl(var(--primary))/0.7] transition-colors min-h-[180px]"
            >
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary))]">For Medical, Dental & Pharma</p>
                <p className="mt-2 font-display text-base uppercase tracking-[0.02em] text-foreground leading-tight">Clinic, hospital or pharma brand? See our healthcare packages.</p>
                <p className="mt-1.5 text-xs text-muted-foreground">Patient stories, procedure films, compliance-aware production.</p>
              </div>
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-foreground group-hover:text-[hsl(var(--primary))] transition-colors">View medical packages <ArrowRight className="h-3 w-3" /></span>
            </Link>

            <Link
              to="/for/construction-services"
              data-testid="pricing-construction-cta"
              className="group flex flex-col justify-between gap-3 rounded-2xl border border-[hsl(var(--primary))/0.35] bg-[hsl(225,18%,8%)] px-6 py-5 hover:border-[hsl(var(--primary))/0.7] transition-colors min-h-[180px]"
            >
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary))]">For Construction, Trades & Services</p>
                <p className="mt-2 font-display text-base uppercase tracking-[0.02em] text-foreground leading-tight">Builder, plumber, electrician or service brand? See our showcase packages.</p>
                <p className="mt-1.5 text-xs text-muted-foreground">Site-safe crew, licensed partner drone pilots. Project, capability & lead-gen content.</p>
              </div>
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-foreground group-hover:text-[hsl(var(--primary))] transition-colors">View showcase packages <ArrowRight className="h-3 w-3" /></span>
            </Link>
          </div>
        </div>
      </section>

      {/* Book a call */}
      <section className="section-pad bg-[hsl(225,22%,4%)] border-y border-border">
        <div className="container-site grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <p className="kicker mb-3">Talk to a producer</p>
            <h2 className="font-display uppercase text-foreground text-[clamp(2rem,5vw,3.5rem)] leading-[0.95]">Not sure which package? Book a free 15-min call.</h2>
            <p className="mt-5 text-sm sm:text-base text-muted-foreground leading-relaxed max-w-lg">Rudolf will walk you through the right tier for your brand, run through deliverables and timelines, and answer any technical or budget questions.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild className="h-12 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-110 px-6"><a href={CALENDLY_URL} target="_blank" rel="noreferrer noopener" data-testid="pricing-calendly-cta"><Calendar className="mr-2 h-4 w-4" /> Book a 15-min call</a></Button>
              <Button asChild variant="ghost" className="h-12 rounded-full border border-border bg-card/60 hover:bg-card px-5"><a href={`mailto:${SITE.email}`}><Mail className="mr-2 h-4 w-4" /> Email us</a></Button>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="rounded-2xl border border-border bg-card overflow-hidden">
              <iframe data-testid="pricing-calendly-iframe" src={`${CALENDLY_URL}?hide_event_type_details=1&hide_gdpr_banner=1&background_color=0d1015&text_color=ede5d3&primary_color=d6a04a`} title="Book a call" className="w-full h-[520px] bg-black" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-pad bg-[hsl(225,22%,4%)] border-t border-border">
        <div className="container-site">
          <p className="kicker mb-3">Pricing FAQ</p>
          <h2 className="font-display uppercase text-foreground text-3xl sm:text-4xl mb-10">Common questions.</h2>
          <FAQ faqs={PRICING_FAQS} />
          <div className="mt-12 text-center">
            <Link to="/contact" className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] text-foreground hover:text-[hsl(var(--primary))] transition-colors">Have a different question? Get in touch <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
